import { useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import { fallbackLanguage } from "i18n";
import { useKeycloak } from "providers/KeycloakProvider";

//with 1 sub portfolio depth
export const PORTFOLIO_BASIC_FIELDS = gql`
  fragment PortfolioBasicFields on Portfolio {
    id
    name
    status
    shortName
    modelPortfolio {
      code
      name
      linkedPortfolio {
        id
        shortName
        name
      }
    }
    extPortfolioIds {
      name
      extId
    }
    type {
      code
      name
      namesAsMap
    }
    currency {
      id
      securityCode
      amountDecimalCount
    }
    portfolioGroups {
      id
      code
    }
    parentPortfolios {
      id
    }
    portfolios {
      id
      name
      status
      shortName
      currency {
        securityCode
        amountDecimalCount
      }
      portfolioGroups {
        id
        code
      }
    }
  }
`;

//maximum of 2 sub portfolio depth
export const CONTACT_INFO_QUERY = gql`
  ${PORTFOLIO_BASIC_FIELDS}
  query GetContactInfo($contactId: Long) {
    contact(id: $contactId) {
      id
      contactId
      name
      classification {
        id
        code
      }
      language {
        locale
      }
      representativeTags {
        representatives
      }
      representees(onlyDirectRepresentees: true) {
        name
        id
        contactId
        status
        portfolios {
          ...PortfolioBasicFields
        }
        representees {
          name
          contactId
        }
      }
      assetManagerPortfolios {
        primaryContact {
          contactId
          name
        }
      }
      portfolios {
        ...PortfolioBasicFields
      }
    }
  }
`;

//maximum of 2 sub portfolio depth
export const CONTACT_INFO_QUERY_WITHOUT_REPS = gql`
  ${PORTFOLIO_BASIC_FIELDS}
  query GetContactInfo($contactId: Long) {
    contact(id: $contactId) {
      id
      contactId
      classification {
        id
        code
      }
      name
      language {
        locale
      }
      representativeTags {
        representatives
      }
      assetManagerPortfolios {
        primaryContact {
          contactId
          name
        }
      }
      portfolios {
        ...PortfolioBasicFields
      }
    }
  }
`;

export enum PortfolioStatus {
  Active = "A",
  Passive = "P",
  Closed = "C",
}

export enum PortfolioGroups {
  CANCEL_ORDER = "CP_CANCEL",
  DEPOSIT = "CP_DEPOSIT",
  WITHDRAW = "CP_WITHDRAWAL",
  TRADE = "CP_TRADING",
  HIDE = "CP_HIDE_PF",
  MONTHLY_INVESTMENTS = "CP_MONTHLYINVESTMENTS",
  MONTHLY_SAVINGS = "CP_MONTHLYSAVINGS",
}

export interface PortfolioGroup {
  id: number;
  code: PortfolioGroups;
}

export interface Representee {
  id: number;
  name: string;
  status: string;
  contactId: string;
  portfolios: Portfolio[];
  representees: [];
}

export interface AssetManagerPortfolios {
  primaryContact: {
    contactId: string;
    name: string;
  };
}

export interface Portfolio {
  id: number;
  name: string;
  status: string;
  shortName: string;
  modelPortfolio: {
    code: string;
    name: string;
    linkedPortfolio: {
      id: number;
      shortName: string;
      name: string;
    };
  };
  parentPortfolios: {
    id: number;
  }[];
  extPortfolioIds: {
    name: string;
    extId: string;
  }[];
  type: PortfolioType;
  portfolios: Portfolio[]; //sub portfolios
  currency: {
    securityCode: string;
    amountDecimalCount: number;
  };
  portfolioGroups: PortfolioGroup[];
}

interface PortfolioType {
  code: string;
  name: string;
  namesAsMap: Record<string, string>;
}

interface Classification {
  id: number;
  code: string;
}

export interface ContactInfoQuery {
  contact?: {
    id: number;
    contactId: string;
    classification: Classification;
    name: string;
    representees: Representee[];
    representativeTags: {
      representatives: Record<string, string>;
    };
    assetManagerPortfolios: AssetManagerPortfolios[];
    language: {
      locale: string;
    };
    portfolios?: Portfolio[];
  };
}

export const useGetContactInfo = (
  callAPI = false,
  id?: string | number,
  runWithReps = false,
  skip?: boolean
) => {
  const { linkedContact } = useKeycloak();
  const { loading, error, data, refetch } = useQuery<ContactInfoQuery>(
    runWithReps ? CONTACT_INFO_QUERY : CONTACT_INFO_QUERY_WITHOUT_REPS,
    {
      variables: {
        contactId: id?.toString() || linkedContact?.toString(),
      },
      fetchPolicy: callAPI ? "cache-and-network" : "cache-first",
      skip,
    }
  );
  const activeAndPassivePortfolios = useMemo(
    () => (!data?.contact?.portfolios?.length ? [] : removeClosed(data?.contact?.portfolios)),
    [data?.contact?.portfolios]
  );

  return {
    loading: loading,
    error: error,
    data: data && {
      contactId: data?.contact?.id,
      _contactId: data?.contact?.contactId,
      portfolios: activeAndPassivePortfolios,
      classification: data?.contact?.classification,
      representativeTags: data?.contact?.representativeTags,
      locale: data?.contact?.language?.locale || fallbackLanguage,
      representees: data?.contact?.representees?.filter((x) => x.status === "A"),
      // all contact portfolios have same currency
      portfoliosCurrency: data?.contact?.portfolios?.[0]?.currency?.securityCode,
      assetManagerPortfolios: data?.contact?.assetManagerPortfolios,
      name: data?.contact?.name,
    },
    refetch,
  };
};

/**
 * Removes closed portfolios (recursively on all sub portfolios as well).
 * @param portfolios Raw portfolio data from FA
 * @returns active/pending portfolios
 */
export const removeClosed = (portfolios: Portfolio[]) => {
  const result = [] as Portfolio[];
  portfolios?.forEach((portfolio) => {
    if (
      portfolio.status !== PortfolioStatus.Closed &&
      portfolio.status !== PortfolioStatus.Passive
    ) {
      const portfolios = removeClosed(portfolio.portfolios);
      result.push({
        ...portfolio,
        portfolios,
      });
    }
  });
  return result;
};
